/**
 * Church Builder Pricing page (with plan selection bug fixed)
 * The CTAs now carry plan + interval into the register flow.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Check, ArrowRight } from "lucide-react";

export default function ChurchBuilderPricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api.get("/church-builder/plans").then((r) => setPlans(r.data));
    api.get("/church-builder/features").then((r) => setFeatures(r.data));
  }, []);

  const labelFor = (key) => features.find((f) => f.key === key)?.label || key;

  const choosePlan = (planId) => {
    // Carry plan + interval through to the registration step so the church
    // can be billed against the correct Stripe price after signup.
    navigate(`/church-builder/register?plan=${encodeURIComponent(planId)}&interval=${billing}`);
  };

  return (
    <div data-testid="cb-pricing" className="bg-[#0A0A0A] text-white">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="label-eyebrow mb-3">Pricing</div>
        <h1 className="font-serif-display text-5xl">Simple plans for <span className="italic text-[#D4AF37]">every ministry</span>.</h1>
        <p className="text-neutral-300 mt-4 max-w-2xl">7-day free trial on every paid plan — we collect card details upfront and only charge after the trial ends. Cancel any time.</p>

        <div className="mt-10 inline-flex border border-white/10 rounded-full p-1">
          {["monthly", "annual"].map((b) => (
            <button key={b} data-testid={`billing-${b}`} onClick={() => setBilling(b)}
              className={`px-4 py-1.5 text-[10px] tracking-widest uppercase rounded-full ${billing === b ? "bg-[#D4AF37] text-black" : "text-neutral-400"}`}>
              {b} {b === "annual" && <span className="opacity-60">· save ~17%</span>}
            </button>
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const price = billing === "annual" ? p.price_annual : p.price_monthly;
            const period = billing === "annual" ? "/yr" : "/mo";
            const seatCopy = p.max_editors === 0 ? "Unlimited editors" : `${p.max_editors} editor${p.max_editors === 1 ? "" : "s"}`;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-xl p-7 border ${p.featured ? "border-[#D4AF37]" : "border-white/10"}`} style={{ background: p.featured ? "rgba(212,175,55,0.05)" : "rgba(255,255,255,0.02)" }}>
                {p.featured && <div className="text-[10px] tracking-widest uppercase text-[#D4AF37] mb-2">Most popular</div>}
                <div className="font-serif-display text-3xl" data-testid={`plan-name-${p.id}`}>{p.name}</div>
                <div className="text-neutral-400 text-sm mt-1">{p.tagline}</div>
                <div className="mt-6 font-serif-display text-5xl text-[#D4AF37]">£{price}<span className="text-base text-neutral-500">{period}</span></div>
                <div className="text-[10px] tracking-widest uppercase text-neutral-500 mt-1">{seatCopy} · {p.trial_days}-day trial</div>
                <ul className="mt-6 space-y-2 max-h-72 overflow-auto">
                  {(p.feature_keys || []).map((k) => (
                    <li key={k} className="flex gap-2 text-sm text-neutral-200"><Check size={14} className="text-[#D4AF37] mt-1 flex-shrink-0" />{labelFor(k)}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid={`plan-cta-${p.id}`}
                  onClick={() => choosePlan(p.id)}
                  className={`mt-8 w-full inline-flex justify-center items-center gap-2 px-5 py-3 rounded text-xs tracking-widest uppercase font-semibold ${p.featured ? "btn-gold" : "btn-ghost-gold"}`}
                >
                  Start 7-day trial <ArrowRight size={12} />
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-neutral-500 mt-10 max-w-2xl">After your 7-day trial ends, Stripe automatically charges the card on file at the price shown. You can update or cancel any time from your dashboard.</p>
      </section>
    </div>
  );
}
